// The media viewer's gestures and paging, without React (web/src/components/MessageList.tsx's Lightbox): a post or
// message with several pictures opens them as one set, swiped (or arrowed) through; a drag down closes. Plain
// functions, so lightboxLogic.test.ts checks them and the native viewers can follow the same numbers.
import { SWIPE } from "./mobile.ts";

/** What one touch drag on the viewer is: paging sideways, pulling down to close, or nothing (a tap, a drag up). */
export type DragAxis = "page" | "pull" | "none";

/**
 * Decides a drag once it has moved past the slop: mostly sideways pages (only when there is more than one item),
 * mostly downward pulls to close. Undecided (null) until then.
 */
export function dragAxis(dx: number, dy: number, count: number): DragAxis | null {
  if (Math.abs(dx) < SWIPE.slop && Math.abs(dy) < SWIPE.slop) return null;
  if (count > 1 && Math.abs(dx) > Math.abs(dy) * 1.2) return "page";
  if (dy > 0 && dy >= Math.abs(dx) * 1.5) return "pull";
  return "none";
}

/** A pull this far down closes the viewer (px), or a quick flick that is shorter (`SWIPE.flickSpeed`, `SWIPE.flickMin`). */
export const PULL_TO_CLOSE = 120;

/** Does a pull of `dy` px, lifted after `ms`, close the viewer? Past `PULL_TO_CLOSE`, or a fast enough flick. */
export function closesOnPull(dy: number, ms: number): boolean {
  const fast = dy / Math.max(1, ms) > SWIPE.flickSpeed && dy > SWIPE.flickMin;
  return dy > PULL_TO_CLOSE || fast;
}

/** How far the picture follows a sideways drag: one to one, but only a third of the way past the first or last. */
export function pageOffset(dx: number, index: number, count: number): number {
  const pastStart = index === 0 && dx > 0;
  const pastEnd = index === count - 1 && dx < 0;
  return pastStart || pastEnd ? dx / 3 : dx;
}

/**
 * Where a sideways drag lands when the finger lifts: the next or previous item when dragged past `SWIPE.commitShare`
 * of the width or flicked fast enough, else where it was. Never past either end.
 */
export function pageAfterDrag(index: number, count: number, dx: number, width: number, ms: number): number {
  const fast = Math.abs(dx) / Math.max(1, ms) > SWIPE.flickSpeed && Math.abs(dx) > SWIPE.flickMin;
  if (Math.abs(dx) < width * SWIPE.commitShare && !fast) return index;
  return clampPage(index + (dx < 0 ? 1 : -1), count);
}

/** An index kept inside 0…count−1. */
export const clampPage = (i: number, count: number): number => Math.max(0, Math.min(count - 1, i));

/** "2 / 4" under a set; nothing for one item. */
export const pageLabel = (index: number, count: number): string => (count > 1 ? `${index + 1} / ${count}` : "");

/**
 * The browser history entry the viewer adds while it's open, so Back (a phone's back swipe, the Back button, the
 * Android back key) closes it instead of leaving the page. It keeps the router's own state and adds a mark.
 */
export const VIEWER_STATE_KEY = "bloomViewer";
export function viewerState(current: unknown, mark: string): Record<string, unknown> {
  return { ...(current && typeof current === "object" ? (current as Record<string, unknown>) : {}), [VIEWER_STATE_KEY]: mark };
}
/** Is the history entry the one this viewer added? */
export const isViewerState = (state: unknown, mark: string): boolean =>
  !!state && typeof state === "object" && (state as Record<string, unknown>)[VIEWER_STATE_KEY] === mark;

// ---- Zoom (#371) ----------------------------------------------------------------------------------------------------
// A picture is drawn as translate(x, y) scale(scale) about the centre of the viewer. Every point below (a pinch's
// focus, a double tap) is measured from that centre, in px. The cases are shared/cases/lightbox.json.

/** The zoom numbers. */
export const ZOOM = {
  /** The picture fitted to the screen. */
  fit: 1,
  /** The most a pinch or a double tap reaches. */
  max: 4,
  /** A double tap on a picture at fit goes here. */
  doubleTap: 2.5,
  /** The least a pinch shows while the fingers are down; letting go springs back to `fit`. */
  floor: 0.5,
  /** Up to here counts as fit: letting go there snaps to fit, and a drag is a page or a pull. */
  snap: 1.05,
  /** A pan this close to an edge (px) is at the edge. */
  edge: 0.5,
} as const;

/** How the picture is drawn: scale about the centre, then moved by (x, y) px. */
export interface ZoomView {
  scale: number;
  x: number;
  y: number;
}
/** The viewer and the picture fitted into it, in px. */
export interface ZoomBox {
  viewW: number;
  viewH: number;
  fitW: number;
  fitH: number;
}
export const FIT_VIEW: ZoomView = { scale: ZOOM.fit, x: 0, y: 0 };

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));
const finite = (n: number, otherwise: number): number => (Number.isFinite(n) ? n : otherwise);

/** Is the picture zoomed in, so that a drag pans it? A scale that is not a number, or at most `ZOOM.snap`, is not. */
export const isZoomed = (scale: number): boolean => scale > ZOOM.snap;

/** How far the picture can move from the centre each way (px) at this scale: none along a side that still fits. */
export function panLimit(box: ZoomBox, scale: number): { x: number; y: number } {
  const s = finite(scale, ZOOM.fit);
  const fitW = Math.min(box.fitW, box.viewW);
  const fitH = Math.min(box.fitH, box.viewH);
  return { x: Math.max(0, (fitW * s - box.viewW) / 2), y: Math.max(0, (fitH * s - box.viewH) / 2) };
}

/** The view with its pan kept inside the picture's edges at its scale. */
function kept(scale: number, x: number, y: number, box: ZoomBox): ZoomView {
  const limit = panLimit(box, scale);
  return { scale, x: clamp(finite(x, 0), -limit.x, limit.x) + 0, y: clamp(finite(y, 0), -limit.y, limit.y) + 0 };
}

/**
 * The view at `scale` that keeps the content under the focus (fx, fy) where it was, then moves by (dx, dy): the
 * fingers of a pinch drifting together. Pan is kept inside the edges.
 */
function zoomedAbout(from: ZoomView, scale: number, fx: number, fy: number, dx: number, dy: number, box: ZoomBox): ZoomView {
  const s0 = finite(from.scale, ZOOM.fit);
  const x = fx + dx - (scale * (fx - finite(from.x, 0))) / s0;
  const y = fy + dy - (scale * (fy - finite(from.y, 0))) / s0;
  return kept(scale, x, y, box);
}

/**
 * While two fingers are down: the view for a pinch that started at `start`, with the fingers now `ratio` times as far
 * apart, around the focus (fx, fy) where they started, and their middle moved by (dx, dy). The scale goes from
 * `ZOOM.floor` to `ZOOM.max`; a ratio that is not a number changes nothing.
 */
export function pinchView(start: ZoomView, ratio: number, fx: number, fy: number, dx: number, dy: number, box: ZoomBox): ZoomView {
  const s0 = finite(start.scale, ZOOM.fit);
  const scale = clamp(s0 * finite(ratio, 1), ZOOM.floor, ZOOM.max);
  return zoomedAbout(start, scale, fx, fy, dx, dy, box);
}

/** When the fingers lift: below fit or up to `ZOOM.snap` goes to fit, above `ZOOM.max` is cut to it. */
export function settleView(v: ZoomView, box: ZoomBox): ZoomView {
  const scale = clamp(finite(v.scale, ZOOM.fit), ZOOM.fit, ZOOM.max);
  return scale <= ZOOM.snap ? FIT_VIEW : kept(scale, v.x, v.y, box);
}

/** A double tap at (tx, ty): back to fit when zoomed, else in to `ZOOM.doubleTap` around the tapped point. */
export function doubleTapView(v: ZoomView, tx: number, ty: number, box: ZoomBox): ZoomView {
  return isZoomed(v.scale) ? FIT_VIEW : zoomedAbout(v, ZOOM.doubleTap, tx, ty, 0, 0, box);
}

/** One finger dragging a zoomed picture by (dx, dy), kept inside its edges. */
export function panView(v: ZoomView, dx: number, dy: number, box: ZoomBox): ZoomView {
  return kept(finite(v.scale, ZOOM.fit), v.x + dx, v.y + dy, box);
}

/** What a drag does to a picture that may be zoomed: the plain `DragAxis`, or `pan`. */
export type ZoomDrag = DragAxis | "pan";

/**
 * Decides a one-finger drag, once past the slop (null until then). At fit it is `dragAxis`. Zoomed in, the drag pans
 * the picture. It pages or pulls only when the picture is already at the edge the drag pushes against: a sideways drag
 * at the left edge going right, or at the right edge going left (a set only); a drag down at the top edge. A side that
 * the picture does not fill has no pan, so it is always at its edge. Decided once; the finger then keeps that job.
 */
export function zoomedDragAxis(dx: number, dy: number, count: number, v: ZoomView, box: ZoomBox): ZoomDrag | null {
  const axis = dragAxis(dx, dy, count);
  if (axis === null || !isZoomed(v.scale)) return axis;
  const limit = panLimit(box, v.scale);
  if (axis === "page") {
    const room = dx > 0 ? limit.x - v.x : v.x + limit.x;
    return room > ZOOM.edge ? "pan" : "page";
  }
  if (axis === "pull") return limit.y - v.y > ZOOM.edge ? "pan" : "pull";
  return "pan";
}
